import React from 'react';
import { EagerImagesContext } from './leadImage';
import { LAZY_SIZES, responsive } from './responsiveImage';
import { LEAD_IMAGE_SIZES, pictureSrcSet } from '@xshop/shared';
import { BundleHotspotsContext } from './bundleContext';
import type { ElementNodeDto } from '@xshop/shared';
import { useElementBox, useElementRender, type ElementBoxAttrs, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp, textProp, urlProp, type ElementProps } from './elementProps';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import { readVideoFile, readVideoId, videoEmbedUrl, VIDEO_PROVIDERS, type VideoPlayback } from './videoEmbed';
import { wordmarkOf } from './storeWordmark';
import './elementPicture.css';
import './elementWordmark.css';

// Image, icon, video and audio. Every address any of them points at has been through `isSafeUrl`, and
// a merchant who has named nothing yet gets a box they can still select rather than a broken request.

/** The smallest an icon may be drawn, and the largest, so a size typed in by hand cannot break a row. */
const MIN_ICON = 8;
const MAX_ICON = 512;

/** How thin or heavy a glyph's line may be drawn; past four a lucide outline fills in on itself. */
const MIN_STROKE = 1;
const MAX_STROKE = 4;

/** How a picture fills its frame — the *Image fit* row, stored as the `object-fit` it means. */
const FITS = ['cover', 'contain', 'fill', 'none', 'scale-down'] as const;

/** Where a cropped picture keeps its subject: the values the image's Focal point offers. */
const FOCAL_POINTS = ['center', 'top', 'bottom', 'left', 'right', 'top left', 'top right', 'bottom left', 'bottom right'] as const;

/**
 * Where a phone stops and everything else begins, for a picture that has a second crop for one.
 *
 * 767px because that is the width the reference theme cuts its own hero art at, and because the crop
 * is the point: a landscape photograph shown at 1.22 on a phone keeps the frame and loses the subject.
 * A merchant who names no second file gets exactly what they had — one `img`, no wrapper.
 */
const PHONE_MEDIA = '(max-width: 767px)';

/**
 * Reads a setting that names a CSS value, and nothing at all when a tree older than the setting stored
 * none — which is what keeps the styles a merchant pinned by hand the only answer on their saved page.
 */
function cssChoice<T extends string>(props: ElementProps, key: string, allowed: readonly T[]): T | undefined {
  const value = textProp(props, key);
  return (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

// Draws a picture, or the empty frame that stands in for one until the merchant has chosen a file.
export const ImageElement: ElementComponent = ({ node }) => {
  const hotspots = React.useContext(BundleHotspotsContext);
  const eager = React.useContext(EagerImagesContext);
  const box = useElementBox(node);
  const { editing, store } = useElementRender();
  const url = urlProp(node.props, 'src', true);
  const alt = textProp(node.props, 'alt');
  // A store with no logo yet shows its own name, never the theme's sample logo — not even while the store loads.
  const wordmark = wordmarkOf(node, store);
  if (wordmark !== null) return <span {...box} className={[box.className, 'xe-wordmark'].filter(Boolean).join(' ')} dir="auto">{wordmark}</span>;
  // A shopper never sees a hole where a photograph should be; a merchant has to, or they cannot click it.
  if (!url) {
    if (!editing) return null;
    return <div {...box} role="img" aria-label={alt || 'Add an image'} data-empty="image" />;
  }
  const fit = cssChoice(node.props, 'fit', FITS);
  const focus = cssChoice(node.props, 'focus', FOCAL_POINTS);
  const phone = urlProp(node.props, 'mobileSrc', true);
  const isLead = eager.lead === node.id;
  // The lead picture fills the screen; the header's (a logo) is small; any other is as wide as it is laid out.
  const sizes = isLead ? LEAD_IMAGE_SIZES : eager.eager.has(node.id) ? '320px' : LAZY_SIZES;
  const picture = (
    <img
      {...box}
      {...responsive(url, sizes)}
      alt={alt}
      title={textProp(node.props, 'title') || undefined}
      loading={eager.eager.has(node.id) ? 'eager' : 'lazy'}
      decoding={isLead ? 'sync' : 'async'}
      // React 18 does not know fetchPriority, so it goes on as the lowercase attribute the browser reads.
      {...(isLead ? { fetchpriority: 'high' } as Record<string, string> : {})}
      style={fit || focus ? { ...(fit ? { objectFit: fit } : {}), ...(focus ? { objectPosition: focus } : {}) } : undefined}
    />
  );
  // `display: contents` on the wrapper, so a second crop costs the layout nothing: the `img` keeps the
  // box, the class and the parent it had, and every rule already written against it still matches.
  const rendered = !phone ? picture : (
    <picture className="xe-picture">
      <source media={PHONE_MEDIA} srcSet={pictureSrcSet(phone) ?? phone} sizes={pictureSrcSet(phone) ? (isLead ? LEAD_IMAGE_SIZES : LAZY_SIZES) : undefined} />
      {picture}
    </picture>
  );
  if (!hotspots || !boolProp(node.props, 'bundleHotspots')) return rendered;
  return <div className="xe-bundle-image">{rendered}
    {hotspots.items.map((item, index) => <button key={item.id} type="button" className="xe-bundle-hotspot"
      style={{ left: `${item.x}%`, top: `${item.y}%` }} aria-label={`${index + 1}. ${item.title}`} title={item.title}
      aria-pressed={hotspots.active === item.id} onClick={() => hotspots.select(item.id)}>{index + 1}</button>)}
  </div>;
};

/**
 * Draws one glyph from the engine's own set, at the size and the line weight the merchant asked for.
 *
 * The accessible label is what a screen reader hears in place of the picture. A glyph nobody named is
 * decoration and stays hidden from one; a glyph a merchant *did* name is meaning, so the span becomes
 * an image with that name and the drawing inside it stays hidden, which is how the name is read once.
 */
export const IconElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const Glyph = ELEMENT_ICONS[textProp(node.props, 'name')] ?? ELEMENT_FALLBACK_ICON;
  const size = Math.min(MAX_ICON, Math.max(MIN_ICON, Math.round(numberProp(node.props, 'size', 24))));
  const stroke = Math.min(MAX_STROKE, Math.max(MIN_STROKE, numberProp(node.props, 'strokeWidth', 2)));
  const label = textProp(node.props, 'label');
  // The *Icon color* row when a merchant used it; the colour the styles gave the span when they did not.
  const paint = textProp(node.props, 'color') || 'currentColor';
  return (
    <span {...box} role={label === '' ? undefined : 'img'} aria-label={label === '' ? undefined : label}>
      <Glyph size={size} color={paint} strokeWidth={stroke} aria-hidden="true" />
    </span>
  );
};

// Reads the four playback flags a video carries, whether they were stored flat or under `playback`.
const readPlayback = (props: ElementProps): VideoPlayback => {
  const nested = (props.playback ?? {}) as ElementProps;
  const merged = { ...nested, ...props };
  return {
    autoplay: boolProp(merged, 'autoplay'),
    loop: boolProp(merged, 'loop'),
    muted: boolProp(merged, 'muted', true),
    controls: boolProp(merged, 'controls', true),
    showTitle: boolProp(merged, 'showTitle')
  };
};

/**
 * Draws a video: a hosted file in the browser's own player, or a provider's frame built from its id.
 *
 * *Platform* decides which, because that is the row a merchant chose from and the three Add-panel cards
 * each set it. A YouTube address pasted into *Source* is read for its id rather than handed to `<video>`
 * as a file — one Source row serving all three platforms is only honest if the platform reads it.
 */
export const VideoElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const provider = choiceProp(node.props, 'provider', VIDEO_PROVIDERS, 'youtube');
  const playback = readPlayback(node.props);
  const file = readVideoFile(urlProp(node.props, 'src'));
  const id = readVideoId(textProp(node.props, 'src'), provider);
  if (provider === 'file' || (file && !id)) {
    return <FileVideo box={box} node={node} src={file} playback={playback} />;
  }
  const embed = id ? videoEmbedUrl(provider, id, playback) : null;
  if (!embed) return <EmptyVideo box={box} node={node} />;
  return (
    <div {...box}>
      <iframe
        src={embed}
        title="Video"
        loading="lazy"
        allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );
};

/** What a hosted-file player needs that its element already resolved, so it resolves nothing twice. */
type FileVideoProps = {
  box: ElementBoxAttrs;
  node: ElementNodeDto;
  src: string | undefined;
  playback: VideoPlayback;
};

// Draws a player with no video yet: its poster picture when it has one, else the builder's empty slot.
const EmptyVideo: React.FC<{ box: ElementBoxAttrs; node: ElementNodeDto }> = ({ box, node }) => {
  const poster = urlProp(node.props, 'poster', true);
  if (!poster) return <div {...box} data-empty="video" />;
  return <div {...box} className={[box.className, 'xe-video-poster'].filter(Boolean).join(' ')}><img src={poster} alt="" /></div>;
};

// Draws a file the merchant hosts themselves in the browser's own player.
const FileVideo: React.FC<FileVideoProps> = ({ box, node, src, playback }) => {
  if (!src) return <EmptyVideo box={box} node={node} />;
  return (
    <video
      {...box}
      src={src}
      poster={urlProp(node.props, 'poster', true)}
      controls={playback.controls}
      autoPlay={playback.autoplay}
      loop={playback.loop}
      muted={playback.muted || playback.autoplay}
      playsInline
    />
  );
};

// Draws one hosted audio track in the browser's own player, inside the element's own strip.
export const AudioElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const src = urlProp(node.props, 'src');
  if (!src) return <div {...box} data-empty="audio" />;
  return (
    <div {...box}>
      <audio src={src} controls preload="none" />
    </div>
  );
};
